import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';

type SmartFolderSyncActionsValue = {
	/** Mark a folder as syncing. */
	addSyncingFolder: (folderId: string) => void;
	/** Mark a folder as no longer syncing. */
	removeSyncingFolder: (folderId: string) => void;
};

type SmartFolderSyncStore = {
	/** Subscribe to store changes. Returns an unsubscribe function. */
	subscribe: (listener: () => void) => () => void;
	/** Returns the current snapshot of syncing folder IDs. */
	getSnapshot: () => ReadonlySet<string>;
	/** Mark a folder as syncing. */
	addSyncingFolder: (folderId: string) => void;
	/** Mark a folder as no longer syncing. */
	removeSyncingFolder: (folderId: string) => void;
};

function createSyncStore(): SmartFolderSyncStore {
	let syncingFolderIds: ReadonlySet<string> = new Set();
	const listeners = new Set<() => void>();

	const notify = () => {
		listeners.forEach((listener) => listener());
	};

	return {
		subscribe: (listener: () => void) => {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
		getSnapshot: () => syncingFolderIds,
		addSyncingFolder: (folderId: string) => {
			if (syncingFolderIds.has(folderId)) {
				return;
			}
			const next = new Set(syncingFolderIds);
			next.add(folderId);
			syncingFolderIds = next;
			notify();
		},
		removeSyncingFolder: (folderId: string) => {
			if (!syncingFolderIds.has(folderId)) {
				return;
			}
			const next = new Set(syncingFolderIds);
			next.delete(folderId);
			syncingFolderIds = next;
			notify();
		},
	};
}

/**
 * A store-based context for tracking which folders are syncing.
 *
 * Uses `useSyncExternalStore` so that each `useIsFolderSyncing(folderId)`
 * subscriber only re-renders when their specific folder's sync boolean
 * changes — not when any arbitrary folder starts/stops syncing.
 *
 * - `SmartFolderSyncStoreContext` holds the store instance.
 * - `SmartFolderSyncActionsContext` holds stable `add`/`remove` callbacks
 *   for writers (e.g. `useSmartFolderSync`) that never re-render on state change.
 */
const SmartFolderSyncStoreContext = createContext<SmartFolderSyncStore | null>(null);
const SmartFolderSyncActionsContext = createContext<SmartFolderSyncActionsValue>({
	addSyncingFolder: () => {},
	removeSyncingFolder: () => {},
});

export const SmartFolderSyncProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const storeRef = useRef<SmartFolderSyncStore | null>(null);
	if (storeRef.current === null) {
		storeRef.current = createSyncStore();
	}
	const store = storeRef.current;

	const actions = useMemo<SmartFolderSyncActionsValue>(
		() => ({
			addSyncingFolder: store.addSyncingFolder,
			removeSyncingFolder: store.removeSyncingFolder,
		}),
		[store],
	);

	return (
		<SmartFolderSyncStoreContext.Provider value={store}>
			<SmartFolderSyncActionsContext.Provider value={actions}>
				{children}
			</SmartFolderSyncActionsContext.Provider>
		</SmartFolderSyncStoreContext.Provider>
	);
};

const EMPTY_SET: ReadonlySet<string> = new Set();
const EMPTY_SUBSCRIBE = (_cb: () => void) => () => {};
const EMPTY_SNAPSHOT = () => EMPTY_SET;
const SERVER_SNAPSHOT_BOOL = () => false;

/**
 * Returns the set of folder IDs currently being synced.
 *
 * ⚠️  Prefer `useIsFolderSyncing(folderId)` when you only need to know
 * whether a *single* folder is syncing — it avoids re-rendering the consumer
 * whenever *any* folder's sync state changes.
 */
export const useSyncingFolderIds = (): ReadonlySet<string> => {
	const store = useContext(SmartFolderSyncStoreContext);
	return useSyncExternalStore(
		store?.subscribe ?? EMPTY_SUBSCRIBE,
		store?.getSnapshot ?? EMPTY_SNAPSHOT,
		EMPTY_SNAPSHOT,
	);
};

/**
 * Returns `true` when the given `folderId` is currently being synced.
 *
 * Uses `useSyncExternalStore` with a per-folder snapshot so that changes
 * to *other* folders do NOT cause this consumer to re-render.
 */
export const useIsFolderSyncing = (folderId: string): boolean => {
	const store = useContext(SmartFolderSyncStoreContext);

	const subscribe = store?.subscribe ?? EMPTY_SUBSCRIBE;
	const getSnapshot = useCallback(
		() => (store ? store.getSnapshot().has(folderId) : false),
		[store, folderId],
	);

	return useSyncExternalStore(subscribe, getSnapshot, SERVER_SNAPSHOT_BOOL);
};

/** Returns helpers to mark folders as syncing/not-syncing. Used by useSmartFolderSync. */
export const useFolderSyncActions = (): SmartFolderSyncActionsValue => {
	return useContext(SmartFolderSyncActionsContext);
};
